import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { History } from "lucide-react";
import { RunStatusPill } from "./RunStatusPill";
import { TokenStat } from "./TokenStat";
import { EmptyState } from "./EmptyState";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/Table";
import { useNow } from "@/components/ClockProvider";
import { useKeysetQuery } from "@/hooks/useKeyset";
import { useRowNavigation } from "@/hooks/useRowNavigation";
import { qs } from "@/lib/api";
import { S, runTriggerLabel } from "@/lib/strings";
import { cn, errorPrefix, formatDuration, runElapsedMs, timeAgo, truncate } from "@/lib/utils";
import { sinceFor, type RunsUrlFilter } from "@/lib/runFilters";
import type { RunRow } from "@/types/fleet";

const PAGE_SIZE = 50;

/**
 * The runs table, shared by `/runs` and a device's 執行紀錄 tab.
 *
 * One component rather than two near-identical ones: the device tab is the fleet
 * table with `device_id` pinned and its column hidden, and every future change
 * to how a run is summarised then lands in both places at once.
 */
export function RunsTable({
  filter,
  hideDeviceColumn = false,
  emptyBody,
}: {
  filter: RunsUrlFilter;
  hideDeviceColumn?: boolean;
  emptyBody?: string;
}) {
  const navigate = useNavigate();
  const now = useNow();
  const containerRef = useRef<HTMLInputElement>(null);

  const since = useMemo(() => sinceFor(filter.date), [filter.date]);

  const query = useKeysetQuery<RunRow>(
    ["runs", "list", filter],
    (cursor) =>
      `/runs${qs({
        device_id: filter.device_id,
        task_id: filter.task_id,
        status: filter.status,
        dispatch_id: filter.dispatch_id,
        has_error: filter.has_error,
        since,
        limit: PAGE_SIZE,
        cursor,
      })}`,
  );

  // `q` is not a documented parameter on /api/v1/runs — the Overview's failure
  // deep links carry the error prefix in the URL, so it is applied here over the
  // loaded rows rather than sent as a parameter the server may quietly ignore.
  // Matching uses the same prefix rule the Overview groups by, so the row count
  // behind a "12× TIMEOUT" link agrees with the number on the link.
  const rows = useMemo(() => {
    if (!filter.q) return query.items;
    const needle = filter.q.toLowerCase();
    const asPrefix = filter.q.toUpperCase();
    return query.items.filter(
      (r) =>
        errorPrefix(r.error) === asPrefix || (r.error ?? "").toLowerCase().includes(needle),
    );
  }, [query.items, filter.q]);

  const nav = useRowNavigation({
    items: rows,
    getKey: (r) => r.id,
    onOpen: (r) => navigate(`/runs/${r.id}`),
    searchRef: containerRef,
  });

  if (query.isError) {
    return <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} />;
  }

  if (query.isInitialLoading) {
    return (
      <div className="space-y-1.5">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={History}
        title={S.noRunsFiltered}
        body={emptyBody ?? S.noRuns}
        compact
      />
    );
  }

  return (
    <>
      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell className="w-24">{S.colStatus}</TableHeaderCell>
            <TableHeaderCell>{S.colTask}</TableHeaderCell>
            {!hideDeviceColumn && <TableHeaderCell>{S.colDevice}</TableHeaderCell>}
            <TableHeaderCell className="w-20">{S.colTrigger}</TableHeaderCell>
            <TableHeaderCell className="w-16">{S.colSteps}</TableHeaderCell>
            <TableHeaderCell className="w-20">{S.colTokens}</TableHeaderCell>
            <TableHeaderCell className="w-20">{S.colDuration}</TableHeaderCell>
            <TableHeaderCell className="w-28">{S.colStarted}</TableHeaderCell>
            <TableHeaderCell>{S.colError}</TableHeaderCell>
          </tr>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow
              key={r.id}
              ref={nav.registerRow(r.id)}
              tabIndex={0}
              onClick={() => navigate(`/runs/${r.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/runs/${r.id}`);
              }}
              className={cn(
                "cursor-pointer",
                nav.isCursor(i) &&
                  "bg-[rgba(113,112,255,0.08)] outline outline-1 -outline-offset-1 outline-[var(--color-accent)]",
              )}
            >
              <TableCell>
                <RunStatusPill status={r.status} error={r.error} size="sm" />
              </TableCell>

              <TableCell>
                <div className="min-w-0">
                  <div className="truncate text-[13px] text-[var(--color-fg)]">{r.taskName}</div>
                  <div className="truncate font-mono text-[10px] text-[var(--color-fg-faint)]">
                    {r.id}
                  </div>
                </div>
              </TableCell>

              {!hideDeviceColumn && (
                <TableCell>
                  <span className="truncate text-[12px]">{r.device?.label ?? "—"}</span>
                </TableCell>
              )}

              <TableCell>
                <span className="text-[11px]">{runTriggerLabel(r.trigger)}</span>
              </TableCell>

              <TableCell>
                <span className="font-mono text-[11px] tabular-nums">{r.steps}</span>
              </TableCell>

              <TableCell>
                <TokenStat
                  prompt={r.promptTokens}
                  completion={r.completionTokens}
                  reasoning={r.reasoningTokens}
                  className="text-[11px]"
                />
              </TableCell>

              <TableCell>
                <span className="font-mono text-[11px] tabular-nums">
                  {formatDuration(runElapsedMs(r, now))}
                </span>
              </TableCell>

              <TableCell>
                <span className="whitespace-nowrap text-[11px] text-[var(--color-fg-subtle)]">
                  {timeAgo(r.startedAt ?? r.createdAt, now)}
                </span>
              </TableCell>

              <TableCell>
                {r.error ? (
                  <Tooltip content={<span className="font-mono">{r.error}</span>}>
                    <span className="truncate font-mono text-[11px] text-[#fca5a5]">
                      {truncate(r.error, 36)}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="text-[var(--color-fg-faint)]">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="mt-3">
        {query.canLoadMore ? (
          <Button
            size="sm"
            variant="default"
            onClick={query.loadMore}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? S.loading : S.loadMore}
          </Button>
        ) : (
          <span className="text-[11px] text-[var(--color-fg-faint)]">{S.noMore}</span>
        )}
      </div>
    </>
  );
}
